import jsPDF from "jspdf";

// Bliss house style for generated PDFs — shared by the student/agent invoice and
// the agent commission statement so branding, colours and letterhead stay
// consistent. jsPDF is re-exported so callers have a single import surface.
export { jsPDF };

export const BRAND_NAVY = [26, 31, 113];
export const BRAND_PINK = [230, 0, 126];
export const LIGHT_GREY = [245, 245, 245];

export const ASSET_LOGO = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";
export const ASSET_FOOTER = process.env.PUBLIC_URL + "/assets/img/footer.png";

// autoTable styling: navy header, light-grey zebra striping, thin grey grid.
export const houseTableDefaults = {
  theme: "grid",
  margin: { left: 40, right: 40 },
  styles: {
    fontSize: 10,
    cellPadding: 6,
    textColor: 40,
    lineColor: [180, 180, 180],
    lineWidth: 0.5,
  },
  headStyles: {
    fillColor: BRAND_NAVY,
    textColor: 255,
    fontStyle: "bold",
  },
  alternateRowStyles: {
    fillColor: LIGHT_GREY,
  },
};

// "Label: value" with the first two characters of the label in brand pink and
// the rest in navy (the recurring Bliss accent).
export function brandAccentLabel(pdf, label, value, x, y) {
  pdf.setFont("helvetica", "bold");

  const first = label.slice(0, 2);
  const rest = label.slice(2);

  pdf.setTextColor(...BRAND_PINK);
  pdf.text(first, x, y);
  const offset1 = pdf.getTextWidth(first);

  pdf.setTextColor(...BRAND_NAVY);
  pdf.text(rest + ": ", x + offset1, y);
  const offset2 = pdf.getTextWidth(rest + ": ");

  pdf.setTextColor(40);
  pdf.text(String(value || ""), x + offset1 + offset2, y);
}

// Centred heading with the same two-tone accent applied per word.
export function brandAccentText(pdf, text, xCenter, y, fontSize) {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(fontSize);

  const words = text.split(" ");
  const parts = [];

  words.forEach((word, wordIndex) => {
    if (!word) return;
    if (word.length >= 2) {
      parts.push({ text: word.slice(0, 2), color: BRAND_PINK });
      if (word.length > 2) parts.push({ text: word.slice(2), color: BRAND_NAVY });
    } else {
      parts.push({ text: word, color: BRAND_PINK });
    }
    if (wordIndex < words.length - 1) parts.push({ text: " ", color: BRAND_NAVY });
  });

  const totalWidth = parts.reduce((sum, p) => sum + pdf.getTextWidth(p.text), 0);
  let x = xCenter - totalWidth / 2;
  parts.forEach((part) => {
    pdf.setTextColor(...part.color);
    pdf.text(part.text, x, y);
    x += pdf.getTextWidth(part.text);
  });
}

// Logo (top-left) + accented title (top-right) + navy rule. Returns the y to
// continue drawing below the rule. Works in portrait or landscape.
export function drawLetterhead(pdf, { title }) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const y = 40;
  const logoWidth = 50;
  const logoHeight = 60;

  pdf.addImage(ASSET_LOGO, "PNG", 40, y, logoWidth, logoHeight);
  brandAccentText(pdf, title, pageWidth - 90, y + logoHeight / 2 + 8, 20);

  const lineY = y + logoHeight + 10;
  pdf.setDrawColor(...BRAND_NAVY);
  pdf.setLineWidth(1);
  pdf.line(40, lineY, pageWidth - 40, lineY);

  return lineY + 18;
}

// Navy rule + centred footer logo at the bottom of the final page.
export function drawFooter(pdf) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  pdf.setPage(pdf.getNumberOfPages());
  pdf.setDrawColor(...BRAND_NAVY);
  pdf.setLineWidth(1);
  pdf.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);
  pdf.addImage(ASSET_FOOTER, "PNG", (pageWidth - 120) / 2, pageHeight - 55, 120, 35);
}
